import dayjs from 'dayjs';
import {
  CircleHelp,
  Dumbbell,
  NotepadText,
  Stethoscope,
  TriangleAlert,
} from 'lucide-react';
import type {ReactNode} from 'react';

import type {BadgeProps} from '@/components/ui/badge';
import type {GetEventsWithDateRangeInput} from '@/services/graphqlHooks';
import {EventSubType, EventType} from '@/services/graphqlHooks';
import {TIME_FORMATS} from '@/utils/timeFormats';
import {cn} from '@/utils/ui';

export const getEventTypeIcon = (eventType: EventType): ReactNode => {
  switch (eventType) {
    case EventType.Casual:
      return <NotepadText />;
    case EventType.Rehabilitation:
      return <Dumbbell />;
    case EventType.Medical:
      return <Stethoscope />;
    case EventType.Unverified:
      return <CircleHelp />;
    default:
      return <TriangleAlert />;
  }
};

type BadgeColor = NonNullable<BadgeProps['color']>;

interface EventTypesClasses {
  icon: string;
  toggle: string;
  badge: string;
  badgeColor: BadgeColor;
  badgeClassName?: string;
}

interface EventSubTypesClasses {
  badge: string;
  badgeColor: BadgeColor;
}

export const getEventTypeClasses = (
  eventType: EventType
): EventTypesClasses => {
  switch (eventType) {
    case EventType.Casual:
      return {
        icon: 'bg-info-500',
        toggle: cn(
          'border transition-all',
          'hover:border-info-600 hover:bg-info-50 hover:text-info-700',
          'data-[state=on]:border-info-600 data-[state=on]:bg-info-50 data-[state=on]:text-info-700'
        ),
        badge: 'border-info-600',
        badgeColor: 'info',
      };
    case EventType.Medical:
      return {
        icon: 'bg-danger-500',
        toggle: cn(
          'border transition-all',
          'hover:border-danger-600 hover:bg-danger-50 hover:text-danger-700',
          'data-[state=on]:border-danger-600 data-[state=on]:bg-danger-50 data-[state=on]:text-danger-700'
        ),
        badge: 'border-danger-600',
        badgeColor: 'danger',
      };
    case EventType.Rehabilitation:
      return {
        icon: 'bg-accent-700',
        toggle: cn(
          'border transition-all',
          'hover:border-accent-700 hover:bg-accent-50 hover:text-accent-800',
          'data-[state=on]:border-accent-700 data-[state=on]:bg-accent-50 data-[state=on]:text-accent-800'
        ),
        badge: 'border-accent-700',
        badgeColor: 'accent',
      };
    case EventType.Unverified:
      return {
        icon: 'bg-orange-500',
        toggle: cn(
          'border transition-all',
          'hover:border-orange-500 hover:bg-orange-50 hover:text-orange-700',
          'data-[state=on]:border-orange-500 data-[state=on]:bg-orange-50 data-[state=on]:text-orange-700'
        ),
        badge: 'border-orange-500',
        badgeColor: 'neutral',
        badgeClassName: 'border-orange-500 bg-orange-50 text-orange-700',
      };
    default:
      return {
        icon: 'bg-warning-500',
        toggle: cn(
          'border transition-all',
          'hover:border-warning-500 hover:bg-warning-50 hover:text-warning-700',
          'data-[state=on]:border-warning-500 data-[state=on]:bg-warning-50 data-[state=on]:text-warning-700'
        ),
        badge: 'border-warning-500',
        badgeColor: 'warning',
      };
  }
};

export const getEventSubTypeClasses = (
  eventSubType: EventSubType
): EventSubTypesClasses => {
  switch (eventSubType) {
    case EventSubType.MedicalAppointment:
    case EventSubType.Illness:
    case EventSubType.Hospital:
    case EventSubType.Treatment:
    case EventSubType.MedicalAbnormality:
      return {badge: 'border-danger-600 text-danger-700', badgeColor: 'danger'};
    default:
      return {
        badge: 'border-neutral-800 text-neutral-800',
        badgeColor: 'neutral',
      };
  }
};

export const eventTypesOptions = Object.values(EventType);

export const getEventSubTypeOptionsForSelectedType = (
  type: EventType
): EventSubType[] => {
  switch (type) {
    case EventType.Casual:
    case EventType.Unverified:
      return [EventSubType.None];
    case EventType.Medical:
      return [
        EventSubType.Hospital,
        EventSubType.Illness,
        EventSubType.MedicalAbnormality,
        EventSubType.MedicalAppointment,
        EventSubType.Treatment,
        EventSubType.None,
      ];
    default:
      return [];
  }
};

export const eventsDateRangeInput = (input?: GetEventsWithDateRangeInput) => {
  if (!input) {
    return {
      startDate: dayjs().subtract(3, 'day').format(TIME_FORMATS.awsDate),
      endDate: dayjs().format(TIME_FORMATS.awsDate),
    };
  }

  const startDate = dayjs(input?.startDate).isValid()
    ? input?.startDate
    : undefined;
  const endDate = dayjs(input?.endDate).isValid() ? input?.endDate : undefined;

  return {
    startDate: dayjs(startDate).format(TIME_FORMATS.awsDate),
    endDate: dayjs(endDate).format(TIME_FORMATS.awsDate),
  };
};

export const eventsPageDateRangeInput = (date: string) => {
  const selectedDate = dayjs(date);
  const startDate = selectedDate.isValid()
    ? selectedDate.subtract(1, 'day')
    : undefined;
  const endDate = selectedDate.isValid()
    ? selectedDate.add(1, 'day')
    : undefined;

  return {
    startDate: dayjs(startDate).format(TIME_FORMATS.awsDate),
    endDate: dayjs(endDate).format(TIME_FORMATS.awsDate),
  };
};
